import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

// v3.8 ENEMY FIGHTER — mirror of PlayerFighter for the enemy side.
// Actions: 'idle' | 'windup' | 'attack' | 'brace' | 'cast' | 'hurt'
export default function EnemyFighter({ sprite, color = '#ff2b2b', action, trigger, isBoss, size = 96, children }) {
  const controls = useAnimation();
  const auraControls = useAnimation();
  const runeControls = useAnimation();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      if (action === 'windup') {
        // Rear back — lift + rotate backward
        await controls.start({ x: 18, y: -6, rotate: 8, scale: 1.05, transition: { duration: 0.28, ease: 'easeOut' } });
      } else if (action === 'attack') {
        // Windup then lunge left toward the player
        await controls.start({ x: 20, rotate: 10, scale: 1.08, transition: { duration: 0.14 } });
        if (cancelled) return;
        await controls.start({ x: -80, rotate: -14, scale: 1.14, transition: { duration: 0.14, ease: 'easeIn' } });
        if (cancelled) return;
        await controls.start({ x: 0, rotate: 0, scale: 1, transition: { type: 'spring', stiffness: 260, damping: 15 } });
      } else if (action === 'brace') {
        // Defensive: crouch + tighten
        auraControls.start({ opacity: 0.5, scale: 1.4, transition: { duration: 0.3 } });
        await controls.start({ y: 10, scale: 0.92, transition: { duration: 0.16 } });
        if (cancelled) return;
        await controls.start({ y: 0, scale: 1, transition: { type: 'spring', stiffness: 220, damping: 14 } });
        auraControls.start({ opacity: 0, scale: 0.8, transition: { duration: 0.4 } });
      } else if (action === 'cast') {
        // Eldritch levitate + spin
        runeControls.start({ opacity: 1, rotate: 360, transition: { duration: 0.9, ease: 'linear' } });
        await controls.start({ y: -16, rotate: -6, scale: 1.06, transition: { duration: 0.35 } });
        if (cancelled) return;
        await controls.start({ y: 0, rotate: 0, scale: 1, transition: { type: 'spring', stiffness: 180, damping: 14 } });
        runeControls.start({ opacity: 0, transition: { duration: 0.4 } });
      } else if (action === 'hurt') {
        await controls.start({ x: 22, rotate: -10, transition: { duration: 0.09 } });
        if (cancelled) return;
        await controls.start({ x: 0, rotate: 0, transition: { type: 'spring', stiffness: 320, damping: 12 } });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [action, trigger]);

  return (
    <div className="enemy-fighter-wrap" data-testid="enemy-fighter" data-action={action}>
      {/* Ground shadow */}
      <div className="enemy-fighter-shadow" style={{ borderColor: color }} />

      {/* Aura on brace */}
      <motion.div
        className="enemy-fighter-aura"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={auraControls}
        style={{ background: `radial-gradient(circle, ${color}55 0%, transparent 65%)` }}
      />

      {/* Runic ring for cast/debuff */}
      <motion.div
        className="enemy-fighter-rune"
        initial={{ opacity: 0, rotate: 0 }}
        animate={runeControls}
        style={{ borderColor: color, boxShadow: `0 0 24px ${color}` }}
        aria-hidden="true"
      />

      <motion.div
        className={`enemy-fighter-body ${isBoss ? 'is-boss' : ''}`}
        initial={{ x: 0, y: 0, rotate: 0, scale: 1 }}
        animate={controls}
        style={{
          fontSize: size,
          textShadow: `0 0 26px ${color}, 4px 4px 0 rgba(0,0,0,0.9)`,
          filter: `drop-shadow(0 6px 0 rgba(0,0,0,0.7))`,
        }}
      >
        <div className={`enemy-fighter-idlebob ${isBoss ? 'pose-idle-boss' : 'pose-idle-mob'}`} data-testid="enemy-sprite">
          <span aria-hidden="true">{sprite}</span>
        </div>
        {/* Overlay children (e.g. hit-spark, block-spark from parent) */}
        {children}
      </motion.div>
    </div>
  );
}
